import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ClipboardList,
  Users,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Filter,
  Loader2
} from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { usePageTitle } from '../../hooks/usePageTitle';
import { CohortFinalizationService } from '../../services/cohortFinalizationService';
import { toast } from '../../components/ui/use-toast';

// Interface for cohort data from Firestore
interface PotentialProblemsCohort {
  id: string;
  cohortNumber: number;
  createdByName: string;
  cohortNotes?: string;
  createdAt: Timestamp;
  status: 'IN_REVIEW' | 'APPROVED' | 'COMPLETED';
  stats: {
    totalSellers: number;
    problemsFound: number;
    problemsSolved: number;
  };
}

const PotentialProblemsPage: React.FC = () => {
  usePageTitle('Potential Problems - PPC Farm Mail Tracker');
  
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState<PotentialProblemsCohort[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finalizingCohorts, setFinalizingCohorts] = useState<Set<string>>(new Set());

  // Real-time subscription to cohorts with IN_REVIEW status
  useEffect(() => {
    console.log('🔄 Setting up real-time subscription for IN_REVIEW cohorts...');
    
    const cohortsQuery = query(
      collection(db, 'ppcFarm_cohorts'),
      where('status', 'in', ['IN_REVIEW', 'APPROVED']),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      cohortsQuery,
      (snapshot) => {
        try {
          const cohortData: PotentialProblemsCohort[] = [];
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            const problemsFound = data.stats?.problemsFound || 0;
            const problemsSolved = data.stats?.problemsSolved || 0;
            
            // Include cohorts that:
            // 1. Have problems to solve (problemsFound > 0), OR
            // 2. Have no problems but are ready to be finalized (status === 'APPROVED' and problemsFound === 0)
            const shouldInclude = problemsFound > 0 || (data.status === 'APPROVED' && problemsFound === 0);
            
            if (shouldInclude) {
              cohortData.push({
                id: doc.id,
                cohortNumber: data.cohortNumber,
                createdByName: data.createdByName,
                cohortNotes: data.cohortNotes,
                createdAt: data.createdAt,
                status: data.status,
                stats: {
                  totalSellers: data.stats?.totalSellers || 0,
                  problemsFound: problemsFound,
                  problemsSolved: problemsSolved,
                },
              });
            }
          });

          console.log(`📊 Loaded ${cohortData.length} cohorts for review and finalization (IN_REVIEW or APPROVED)`);
          setCohorts(cohortData);
          setError(null);
        } catch (err) {
          console.error('❌ Error processing cohorts data:', err);
          setError('Failed to process cohorts data');
        } finally {
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('❌ Error in cohorts subscription:', err);
        setError('Failed to load cohorts. Please try again.');
        setIsLoading(false);
      }
    );

    return () => {
      console.log('🔄 Cleaning up cohorts subscription');
      unsubscribe();
    };
  }, []);

  // Format timestamp for display
  const formatTimestamp = (timestamp: Timestamp): string => {
    const date = timestamp.toDate();
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
  };

  // Calculate progress percentage
  const calculateProgress = (problemsSolved: number, problemsFound: number): number => {
    if (problemsFound === 0) return 0;
    return Math.round((problemsSolved / problemsFound) * 100);
  };

  // Get progress status color
  const getProgressColor = (progress: number): string => {
    if (progress === 100) return 'text-green-600 dark:text-green-400';
    if (progress >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Handle cohort card click
  const handleCohortClick = (cohortId: string) => {
    navigate(`/ppc-farm/review-session/${cohortId}`);
  };

  // Handle cohort finalization
  const handleFinalizeCohort = async (cohortId: string, cohortNumber: number) => {
    try {
      // Add cohort to finalizing set
      setFinalizingCohorts(prev => new Set(prev).add(cohortId));

      console.log(`🚀 Starting finalization for Cohort ${cohortNumber} (${cohortId})`);
      
      // Show loading toast
      toast({
        title: "Finalizing Cohort",
        description: `Starting finalization process for Cohort ${cohortNumber}...`,
      });

      // Call the finalization service
      const result = await CohortFinalizationService.finalizeCohort(cohortId);

      if (result.success) {
        // Show success toast with stats
        toast({
          title: "Cohort Finalized Successfully! 🎉",
          description: `Cohort ${cohortNumber} has been moved to the ledgers. ${result.stats?.sellersProcessed || 0} sellers processed.`,
          variant: "default",
        });

        console.log(`✅ Cohort ${cohortNumber} finalized successfully:`, result.stats);
      } else {
        throw new Error(result.error || 'Finalization failed');
      }
    } catch (error) {
      console.error(`❌ Error finalizing cohort ${cohortId}:`, error);
      
      // Show error toast
      toast({
        title: "Finalization Failed",
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: "destructive",
      });
    } finally {
      // Remove cohort from finalizing set
      setFinalizingCohorts(prev => {
        const newSet = new Set(prev);
        newSet.delete(cohortId);
        return newSet;
      });
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Potential Problems
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              VA Dashboard - Cohorts awaiting review
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mt-2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full mt-4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              Potential Problems
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              VA Dashboard - Cohorts awaiting review
            </p>
          </div>
        </div>

        <Card className="border-red-200 dark:border-red-800">
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error Loading Cohorts
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => window.location.reload()} 
              variant="outline" 
              size="sm" 
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Potential Problems
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            VA Dashboard - Cohorts for review and ledger processing
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400">
            <ClipboardList className="h-3 w-3 mr-1" />
            {cohorts.length} Job{cohorts.length !== 1 ? 's' : ''} Pending
          </Badge>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <ClipboardList className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cohorts.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sellers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cohorts.reduce((sum, cohort) => sum + cohort.stats.totalSellers, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Problems</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {cohorts.reduce((sum, cohort) => sum + cohort.stats.problemsFound, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cohorts Grid */}
      {cohorts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              All Caught Up!
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No cohorts are currently awaiting review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cohorts.map((cohort) => {
            const progress = calculateProgress(cohort.stats.problemsSolved, cohort.stats.problemsFound);
            const progressColor = getProgressColor(progress);
            const isCompleted = cohort.status === 'APPROVED';
            const hasNoProblems = cohort.stats.problemsFound === 0;
            const borderColor = isCompleted ? 'border-l-green-500' : 'border-l-yellow-500';
            
            return (
              <Card
                key={cohort.id}
                className={`hover:shadow-md transition-shadow cursor-pointer border-l-4 ${borderColor}`}
                onClick={() => handleCohortClick(cohort.id)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-semibold">
                      Cohort {cohort.cohortNumber}
                    </CardTitle>
                    {isCompleted ? (
                      <Badge variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-900/10 dark:text-green-400">
                        READY TO BE PASTED
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-900/10 dark:text-yellow-400">
                        IN REVIEW
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Created by {cohort.createdByName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    {formatTimestamp(cohort.createdAt)}
                  </div>
                </CardHeader>
                
                <CardContent>
                  {/* Cohort Notes */}
                  {cohort.cohortNotes && (
                    <div className="mb-4 p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                      <p className="text-gray-700 dark:text-gray-300">
                        "{cohort.cohortNotes}"
                      </p>
                    </div>
                  )}

                  {/* Progress Section */}
                  <div className="space-y-3">
                    {hasNoProblems ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Status</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          No Problems Found - Ready to Finalize
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Progress</span>
                          <span className={`font-semibold ${progressColor}`}>
                            {cohort.stats.problemsSolved} / {cohort.stats.problemsFound} Problems Solved
                          </span>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        
                        <div className="text-xs text-gray-500 dark:text-gray-500 text-center">
                          {progress}% Complete
                        </div>
                      </>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-gray-600 dark:text-gray-400">
                          {cohort.stats.totalSellers.toLocaleString()} Sellers
                        </span>
                      </div>
                      {isCompleted ? (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                          disabled={finalizingCohorts.has(cohort.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFinalizeCohort(cohort.id, cohort.cohortNumber);
                          }}
                        >
                          {finalizingCohorts.has(cohort.id) ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Finalizing...
                            </>
                          ) : (
                            <>
                              Paste to Ledger
                              <ArrowRight className="h-3 w-3 ml-1" />
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCohortClick(cohort.id);
                          }}
                        >
                          Review
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PotentialProblemsPage;